import {
  Globe,
  Smartphone,
  Code2,
  Layers,
  Plug,
  Brain,
  Sparkles,
  Bot,
  Settings2,
  MessageSquare,
  Workflow,
  Cloud,
  Gauge,
  Shield,
  Wrench,
  Palette,
  Bug,
  TrendingUp,
  Languages,
  LucideIcon,
} from "lucide-react";

export interface Service {
  id: string;
  icon: LucideIcon;
  title: string;
  description: string;
  longDescription: string;
  tags: string[];
  features: string[];
}

export interface ServiceCategory {
  id: string;
  title: string;
  description: string;
  longDescription: string;
  icon: LucideIcon;
  gradient: string;
  accentColor: string;
  services: Service[];
}

export const serviceCategories: Record<string, ServiceCategory> = {
  webApp: {
    id: "web-app",
    title: "Web & App Development",
    description:
      "Modern, scalable applications built with cutting-edge technologies",
    longDescription:
      "We craft exceptional digital experiences using the latest technologies. From responsive websites to complex web applications, our team delivers solutions that drive business growth and user engagement.",
    icon: Globe,
    gradient: "from-blue-500/20 via-cyan-500/10 to-teal-500/20",
    accentColor: "cyan",
    services: [
      {
        id: "web-development",
        icon: Globe,
        title: "Web Development",
        description:
          "High-performance websites using React, Next.js, and modern frameworks.",
        longDescription:
          "We build blazing-fast, SEO-optimized websites that captivate users and drive conversions. Our modern tech stack ensures your site stays ahead of the curve.",
        tags: ["React", "Next.js", "TypeScript"],
        features: [
          "Responsive design for all devices",
          "SEO optimization built-in",
          "Performance-first architecture",
          "Modern UI/UX patterns",
          "Accessibility compliance",
        ],
      },
      {
        id: "full-stack-web-apps",
        icon: Layers,
        title: "Full Stack Web Apps",
        description:
          "End-to-end solutions with robust backends and seamless frontends.",
        longDescription:
          "Complete web application development from database design to user interface. We handle the entire stack so you can focus on your business.",
        tags: ["Node.js", "PostgreSQL", "REST"],
        features: [
          "Scalable architecture design",
          "Real-time data synchronization",
          "Secure authentication systems",
          "Database optimization",
          "API-first development",
        ],
      },
      {
        id: "website-customization",
        icon: Palette,
        title: "Website Customization",
        description:
          "Tailored modifications to match your brand and business needs.",
        longDescription:
          "Transform your existing website with custom features, design updates, and functionality enhancements that align with your evolving business goals.",
        tags: ["UI/UX", "Branding", "CMS"],
        features: [
          "Custom theme development",
          "Plugin & extension development",
          "Performance improvements",
          "Brand identity integration",
          "Content management solutions",
        ],
      },
      {
        id: "android-app-development",
        icon: Smartphone,
        title: "Android App Development",
        description:
          "Native Android apps with Kotlin and cross-platform solutions.",
        longDescription:
          "Build powerful mobile experiences for Android users. From native Kotlin apps to cross-platform solutions with Flutter, we deliver apps that users love.",
        tags: ["Kotlin", "Flutter", "React Native"],
        features: [
          "Native Android development",
          "Cross-platform solutions",
          "Material Design implementation",
          "Offline-first capabilities",
          "Push notifications & analytics",
        ],
      },
      {
        id: "apis-integrations",
        icon: Plug,
        title: "APIs & Integrations",
        description:
          "Seamless third-party integrations and custom API development.",
        longDescription:
          "Connect your systems with powerful APIs and seamless integrations. We build robust backend services that enable your applications to communicate efficiently.",
        tags: ["REST", "GraphQL", "Webhooks"],
        features: [
          "RESTful API design",
          "GraphQL implementations",
          "Third-party integrations",
          "Webhook systems",
          "API documentation",
        ],
      },
    ],
  },
  aiAutomation: {
    id: "ai-automation",
    title: "AI & Automation",
    description:
      "Intelligent systems that automate workflows and scale your business",
    longDescription:
      "Harness the power of artificial intelligence to transform your operations. Our AI solutions automate complex tasks, provide intelligent insights, and create new possibilities for your business.",
    icon: Brain,
    gradient: "from-purple-500/20 via-pink-500/10 to-rose-500/20",
    accentColor: "purple",
    services: [
      {
        id: "ai-ml-solutions",
        icon: Brain,
        title: "AI / ML Solutions",
        description:
          "Custom machine learning models tailored to your specific use cases.",
        longDescription:
          "Leverage custom machine learning models designed specifically for your business challenges. From predictive analytics to computer vision, we build AI that delivers real results.",
        tags: ["TensorFlow", "PyTorch", "ML"],
        features: [
          "Custom model development",
          "Predictive analytics",
          "Computer vision solutions",
          "Recommendation systems",
          "Model training & deployment",
        ],
      },
      {
        id: "ai-technology-consulting",
        icon: Sparkles,
        title: "AI Technology Consulting",
        description:
          "Strategic guidance on implementing AI across your organization.",
        longDescription:
          "Navigate the AI landscape with expert guidance. We help you identify opportunities, develop strategies, and implement AI solutions that align with your business objectives.",
        tags: ["Strategy", "Assessment", "Roadmap"],
        features: [
          "AI readiness assessment",
          "Technology roadmap planning",
          "Use case identification",
          "ROI analysis",
          "Implementation strategy",
        ],
      },
      {
        id: "ai-agents",
        icon: Bot,
        title: "AI Agents",
        description:
          "Autonomous agents that handle complex tasks and decision-making.",
        longDescription:
          "Deploy intelligent AI agents that work autonomously to complete complex tasks. From customer service to data analysis, our agents learn and improve continuously.",
        tags: ["LangChain", "AutoGPT", "Multi-agent"],
        features: [
          "Autonomous task execution",
          "Multi-agent orchestration",
          "Decision-making systems",
          "Continuous learning",
          "Human-in-the-loop options",
        ],
      },
      {
        id: "ai-fine-tuning",
        icon: Settings2,
        title: "AI Fine-Tuning",
        description:
          "Optimize pre-trained models for your domain-specific requirements.",
        longDescription:
          "Customize powerful AI models for your specific domain. Fine-tuning enables better performance, reduced costs, and more accurate results for your use cases.",
        tags: ["LLMs", "Transfer Learning", "RLHF"],
        features: [
          "Domain-specific training",
          "Performance optimization",
          "Cost reduction strategies",
          "Quality assurance",
          "Deployment pipelines",
        ],
      },
      {
        id: "ai-chatbot",
        icon: MessageSquare,
        title: "AI Chatbot",
        description:
          "Conversational AI powered by GPT and advanced language models.",
        longDescription:
          "Create intelligent conversational experiences with state-of-the-art language models. Our AI chatbots understand context, handle complex queries, and deliver human-like interactions.",
        tags: ["GPT-4", "Claude", "RAG"],
        features: [
          "Natural language understanding",
          "Context-aware responses",
          "Multi-language support",
          "Knowledge base integration",
          "Analytics & insights",
        ],
      },
      {
        id: "rules-based-chatbot",
        icon: Code2,
        title: "Rules Based Chatbot",
        description:
          "Structured conversational flows for predictable customer interactions.",
        longDescription:
          "Build reliable chatbots with predictable behavior using rule-based systems. Perfect for FAQ handling, guided workflows, and structured customer interactions.",
        tags: ["NLP", "Decision Trees", "Intent"],
        features: [
          "Predictable responses",
          "Easy to maintain",
          "Clear conversation flows",
          "Integration ready",
          "Fallback handling",
        ],
      },
      {
        id: "automations-agents",
        icon: Workflow,
        title: "Automations & Agents",
        description:
          "Workflow automation that eliminates repetitive tasks at scale.",
        longDescription:
          "Automate your business processes with intelligent workflows. From simple task automation to complex multi-step processes, we help you work smarter, not harder.",
        tags: ["n8n", "Zapier", "Custom"],
        features: [
          "Process automation",
          "Workflow orchestration",
          "Integration connectors",
          "Error handling",
          "Monitoring & alerts",
        ],
      },
      {
        id: "nlp",
        icon: Languages,
        title: "NLP",
        description:
          "Natural language processing solutions for text analysis and understanding.",
        longDescription:
          "Extract insights from text data with advanced NLP techniques. From sentiment analysis to entity extraction, we help you understand and leverage unstructured text data.",
        tags: ["NLTK", "spaCy", "Transformers"],
        features: [
          "Sentiment analysis",
          "Entity recognition",
          "Text classification",
          "Language translation",
          "Document summarization",
        ],
      },
    ],
  },
  devopsCloud: {
    id: "devops-cloud",
    title: "DevOps & Cloud",
    description: "Scalable infrastructure and streamlined deployment pipelines",
    longDescription:
      "Build robust, scalable infrastructure with modern DevOps practices. We help you deploy faster, scale effortlessly, and maintain reliability across your entire stack.",
    icon: Cloud,
    gradient: "from-orange-500/20 via-amber-500/10 to-yellow-500/20",
    accentColor: "orange",
    services: [
      {
        id: "devops-cloud-services",
        icon: Cloud,
        title: "DevOps & Cloud",
        description:
          "End-to-end cloud architecture with CI/CD and infrastructure as code.",
        longDescription:
          "Implement modern DevOps practices with cloud-native architecture. From CI/CD pipelines to container orchestration, we build infrastructure that scales.",
        tags: ["AWS", "GCP", "Azure"],
        features: [
          "Cloud architecture design",
          "CI/CD pipeline setup",
          "Infrastructure as Code",
          "Container orchestration",
          "Monitoring & logging",
        ],
      },
      {
        id: "speed-optimization",
        icon: Gauge,
        title: "Speed Optimization",
        description:
          "Performance tuning for lightning-fast load times and response.",
        longDescription:
          "Optimize your applications for peak performance. We analyze bottlenecks, implement caching strategies, and fine-tune your infrastructure for speed.",
        tags: ["CDN", "Caching", "Core Web Vitals"],
        features: [
          "Performance auditing",
          "CDN implementation",
          "Caching strategies",
          "Database optimization",
          "Code optimization",
        ],
      },
    ],
  },
  support: {
    id: "support-maintenance",
    title: "Support & Maintenance",
    description:
      "Reliable ongoing support to keep your systems running smoothly",
    longDescription:
      "Keep your systems running at peak performance with our comprehensive support services. We provide proactive maintenance, rapid issue resolution, and continuous improvements.",
    icon: Shield,
    gradient: "from-green-500/20 via-emerald-500/10 to-teal-500/20",
    accentColor: "emerald",
    services: [
      {
        id: "maintenance-support",
        icon: Shield,
        title: "Maintenance & Support",
        description:
          "24/7 monitoring and proactive maintenance for peace of mind.",
        longDescription:
          "Rest easy with our comprehensive maintenance and support services. We monitor your systems around the clock and address issues before they impact your business.",
        tags: ["Monitoring", "Updates", "SLA"],
        features: [
          "24/7 system monitoring",
          "Proactive maintenance",
          "Security updates",
          "Performance monitoring",
          "Dedicated support team",
        ],
      },
      {
        id: "bug-fixes",
        icon: Bug,
        title: "Bug Fixes",
        description:
          "Rapid diagnosis and resolution of software issues and defects.",
        longDescription:
          "Quick and thorough bug resolution to keep your applications running smoothly. Our experienced team diagnoses issues fast and implements lasting fixes.",
        tags: ["Debugging", "QA", "Testing"],
        features: [
          "Rapid diagnosis",
          "Root cause analysis",
          "Thorough testing",
          "Documentation",
          "Prevention strategies",
        ],
      },
      {
        id: "website-customization-support",
        icon: Palette,
        title: "Website Customization",
        description:
          "Continuous improvements and feature additions to your platform.",
        longDescription:
          "Keep your website fresh and competitive with ongoing customization services. We implement new features, update designs, and enhance functionality continuously.",
        tags: ["Enhancements", "Features", "UX"],
        features: [
          "Feature additions",
          "Design updates",
          "UX improvements",
          "Content updates",
          "A/B testing",
        ],
      },
      {
        id: "performance-optimization",
        icon: TrendingUp,
        title: "Performance Optimization",
        description:
          "Ongoing performance monitoring and optimization services.",
        longDescription:
          "Continuous performance optimization to ensure your applications stay fast and efficient. We monitor, analyze, and improve performance metrics over time.",
        tags: ["Performance", "Analytics", "Metrics"],
        features: [
          "Continuous monitoring",
          "Performance analytics",
          "Optimization recommendations",
          "Implementation support",
          "Regular reporting",
        ],
      },
    ],
  },
};

// Helper function to get category by URL slug
export const getCategoryBySlug = (
  slug: string,
): ServiceCategory | undefined => {
  const categoryMap: Record<string, string> = {
    "web-app": "webApp",
    "ai-automation": "aiAutomation",
    "devops-cloud": "devopsCloud",
    "support-maintenance": "support",
  };
  return serviceCategories[categoryMap[slug]];
};

// Helper function to get service by ID within a category
export const getServiceById = (
  categorySlug: string,
  serviceId: string,
): Service | undefined => {
  const category = getCategoryBySlug(categorySlug);
  if (!category) return undefined;
  return category.services.find((s) => s.id === serviceId);
};

// Get all categories as array
export const getAllCategories = (): ServiceCategory[] => {
  return Object.values(serviceCategories);
};
